import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { LineChart, XAxis, YAxis, ReferenceLine } from 'recharts';
import { CHART_MARGIN, toTimestamp } from '../common';
import formatFloatNum from '../../../../shared/utils/floatFormatter';

// График-маркер для отображения значения сенсора в выбранный момент времени
function MarkerChart({ chartData, selectedDate = null, width = 0, height = 0, domain }) {
  const selectedTs = useMemo(() => toTimestamp(selectedDate), [selectedDate]);

  // бинарный поиск значения/ближайшего значения графика для маркера
  const { result: nearestPoint, isAccurate: isAccurateValue } = useMemo(() => {
    if (!selectedTs || !chartData?.length) return {};

    let left = 0;
    let right = chartData.length - 1;
    let result = null;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const point = chartData[mid];

      if (point.timestamp === selectedTs) {
        return { result: point, isAccurate: true };
      }

      if (point.timestamp < selectedTs) {
        result = point;
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    return { result, isAccurate: false };
  }, [chartData, selectedTs]);

  if (selectedTs == null) return null;

  return (
    <LineChart width={width} height={height} data={chartData} margin={{ ...CHART_MARGIN, bottom: 32 }}>
      <XAxis type="number" dataKey="timestamp" domain={domain || ['dataMin', 'dataMax']} scale="time" tick={false} />
      <YAxis domain={['auto', 'auto']} />
      {nearestPoint?.value != null && (
        <ReferenceLine
          x={selectedTs}
          stroke="red"
          strokeWidth={2}
          strokeDasharray="5 5"
          ifOverflow="extendDomain"
          label={
            nearestPoint
              ? {
                  position: 'right',
                  value: `${isAccurateValue ? '' : '~'}${formatFloatNum(nearestPoint.value, 2)}`,
                  fill: 'black',
                  fontSize: 14,
                }
              : undefined
          }
        />
      )}
    </LineChart>
  );
}

MarkerChart.propTypes = {
  chartData: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      timestamp: PropTypes.number,
    }),
  ).isRequired,
  selectedDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  width: PropTypes.number,
  height: PropTypes.number,
  domain: PropTypes.arrayOf(PropTypes.number),
};

MarkerChart.defaultProps = {
  selectedDate: null,
  width: 0,
  height: 0,
  domain: undefined,
};

export default MarkerChart;
